/**
 * InitiateUpgradeCommand - Initiates a plan upgrade via Stripe
 * Follows MediatR pattern (Command + Handler in same file)
 */

import type { ICommand, ICommandHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { UpgradeInitiationResult } from '@/application/queries/subscription/subscription.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Command (Request)
// ============================================================================

export class InitiateUpgradeCommand implements ICommand<UpgradeInitiationResult> {
  readonly type = 'InitiateUpgradeCommand' as const;

  constructor(public readonly targetPlan: string) {}
}

// ============================================================================
// Handler
// ============================================================================

export class InitiateUpgradeCommandHandler
  implements ICommandHandler<InitiateUpgradeCommand, UpgradeInitiationResult>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(command: InitiateUpgradeCommand): Promise<UpgradeInitiationResult> {
    try {
      const response = await this.httpClient.post<BaseApiResponse<any>>(
        '/api/v1/subscription/upgrade',
        { target_plan: command.targetPlan }
      );

      if (!response.succeeded) {
        throw new Error(response.message || 'Failed to initiate upgrade');
      }

      return toCamelCase(response.data ?? {});
    } catch (error: any) {
      if (error.response?.status === 400 || error.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid upgrade request');
      }
      throw error;
    }
  }
}
